import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { z } from 'zod';
import {
  ActivityFeed,
  AdminUser,
  PromoResult,
  Scenario,
  ScenarioResult,
  type ActivityKind,
  type PromoCatalogue,
  type PromoScenarioInput,
  type ScenarioInput,
  type UpdateUserBody,
} from '@exigo/shared';
import { api } from './client';
import { scenarioStore, type ScenarioRecord } from '../lib/scenarioStore';
import { PromoImportResponse, ReferenceData, VersionResponse } from './types';

/** The query key every scenario read/write agrees on, for cache invalidation. */
const SCENARIOS_KEY = ['scenarios'] as const;

export function useReferenceQuery(): UseQueryResult<ReferenceData> {
  return useQuery({
    queryKey: ['reference'],
    queryFn: () => api.get('/reference', ReferenceData),
    staleTime: 5 * 60 * 1000,
  });
}

export function useVersionQuery(): UseQueryResult<VersionResponse> {
  return useQuery({
    queryKey: ['version'],
    queryFn: () => api.get('/version', VersionResponse),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * The only place a scenario is calculated. The engine is server-side only
 * (security.md §14 A04) — this mutation is the entire interface to it.
 */
export function useEvaluateMutation(): UseMutationResult<
  ScenarioResult,
  Error,
  { input: ScenarioInput; guideVersion?: string }
> {
  return useMutation({
    mutationFn: ({ input, guideVersion }) =>
      api.post('/evaluate', ScenarioResult, { input, guideVersion }),
  });
}

/**
 * CSP promotion pricing. Server-side for the same reason as `/evaluate`, plus
 * one more: the band-drop test reads MCI payout bands that never leave the API.
 */
export function usePromotionsMutation(): UseMutationResult<
  PromoResult,
  Error,
  { input: PromoScenarioInput; catalogue?: PromoCatalogue; guideVersion?: string }
> {
  return useMutation({
    mutationFn: ({ input, catalogue, guideVersion }) =>
      api.post('/promotions/evaluate', PromoResult, { input, catalogue, guideVersion }),
  });
}

/**
 * Parses a Partner Center Promotion List export into a fresh catalogue.
 *
 * Parsing is server-side: the family-mapping and grouping rules are guide-edition
 * interpretation, and the response merges in the guide's own MCI risk map, which
 * a customer-pricing file knows nothing about.
 */
export function usePromotionImportMutation(): UseMutationResult<
  { catalogue: PromoCatalogue; skuRowsRead: number },
  Error,
  { csv: string; asAt: string }
> {
  return useMutation({
    mutationFn: ({ csv, asAt }) =>
      api.post('/promotions/import', PromoImportResponse, { csv, asAt }),
  });
}

/**
 * Scenario persistence. These hooks are thin React Query wrappers around
 * `scenarioStore`'s HTTP calls — `scenarioStore` owns the actual request/
 * validation logic in one place; this layer only adds caching, loading state
 * and cross-component invalidation on top of it.
 */

export function useScenariosQuery(): UseQueryResult<ScenarioRecord[]> {
  return useQuery({
    queryKey: SCENARIOS_KEY,
    queryFn: scenarioStore.list,
  });
}

/**
 * One scenario. `null` means "does not exist, or belongs to someone else" —
 * the API never distinguishes the two, see security.md §4 — matching the
 * `null` = "does not exist" contract the rest of the calculator already
 * expects from the sessionStorage version this replaces.
 */
export function useScenarioQuery(id: string): UseQueryResult<ScenarioRecord | null> {
  return useQuery({
    queryKey: [...SCENARIOS_KEY, id],
    queryFn: () => scenarioStore.get(id),
  });
}

export function useCreateScenarioMutation(): UseMutationResult<
  ScenarioRecord,
  Error,
  Parameters<typeof scenarioStore.create>[0]
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: scenarioStore.create,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCENARIOS_KEY });
    },
  });
}

export function useUpdateScenarioMutation(): UseMutationResult<
  ScenarioRecord | null,
  Error,
  { id: string; patch: Parameters<typeof scenarioStore.update>[1] }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => scenarioStore.update(id, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCENARIOS_KEY });
    },
  });
}

export function useDeleteScenarioMutation(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: scenarioStore.remove,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: SCENARIOS_KEY });
    },
  });
}

/**
 * Administration. Every one of these hits `/admin/*`, which the API gates on
 * the Admin role at the route prefix — so these hooks are only ever mounted
 * behind `RequireRole`, and a non-admin calling them would get a 403 rather
 * than data. The client-side gate is convenience; the server's is the boundary.
 */

const ADMIN_USERS_KEY = ['admin', 'users'] as const;
const ADMIN_ACTIVITY_KEY = ['admin', 'activity'] as const;

export function useAdminUsersQuery(): UseQueryResult<AdminUser[]> {
  return useQuery({
    queryKey: ADMIN_USERS_KEY,
    queryFn: () => api.get('/admin/users', z.array(AdminUser)),
  });
}

export function useUpdateUserMutation(): UseMutationResult<
  AdminUser,
  Error,
  { id: number; patch: UpdateUserBody }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, patch }) => api.patch(`/admin/users/${String(id)}`, AdminUser, patch),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ADMIN_USERS_KEY });
      // An approval or role change is itself an activity event, so the feed
      // beside the table is stale the moment this returns.
      void queryClient.invalidateQueries({ queryKey: ADMIN_ACTIVITY_KEY });
    },
  });
}

/** One page of the activity feed. Small enough that the first paint is cheap. */
const ACTIVITY_PAGE_SIZE = 50;

/**
 * The activity feed, paged.
 *
 * `useInfiniteQuery` rather than `useQuery` because the API pages on a keyset
 * (`before` = the oldest id seen so far), not an offset — the feed grows at the
 * head, so offset paging would silently repeat rows that new events pushed down
 * between one page and the next. A keyset cursor is forward-only by nature,
 * which is why this appends pages rather than offering numbered ones.
 */
export function useActivityFeedQuery(
  filters: { userId?: number; kind?: ActivityKind } = {},
): UseInfiniteQueryResult<InfiniteData<ActivityFeed>> {
  return useInfiniteQuery({
    // Filters are part of the key, so switching them refetches from the head
    // rather than showing the previous filter's rows under the new label.
    queryKey: [...ADMIN_ACTIVITY_KEY, filters.userId ?? null, filters.kind ?? null],
    initialPageParam: undefined as number | undefined,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams({ limit: String(ACTIVITY_PAGE_SIZE) });
      if (pageParam !== undefined) params.set('before', String(pageParam));
      if (filters.userId !== undefined) params.set('userId', String(filters.userId));
      if (filters.kind !== undefined) params.set('kind', filters.kind);
      return api.get(`/admin/activity?${params.toString()}`, ActivityFeed);
    },
    // `null` means the server has no older rows; `undefined` is what React
    // Query reads as "no next page".
    getNextPageParam: (lastPage) => lastPage.nextBefore ?? undefined,
  });
}

/** Every scenario across every user — the admin-only cross-user view. */
export function useAllScenariosQuery(): UseQueryResult<ScenarioRecord[]> {
  return useQuery({
    queryKey: ['admin', 'scenarios'],
    queryFn: () => api.get('/admin/scenarios', z.array(Scenario)),
  });
}
